import { ChevronLeft, ChevronRight, Save, Trash2 } from 'lucide-react';
import { Link, useBlocker, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { BundleSettingsPanel } from '@/components/bundle/BundleSettingsPanel';
import { BundleToolbar } from '@/components/bundle/BundleToolbar';
import { useBundleCounts, useBundleTreeActions, useBundleTreeStore, useEditorMeta, useIsDirty } from '@/components/bundle/bundleTreeStore';
import { toGroups } from '@/components/bundle/bundleTypes';
import { DeleteAlertDialog } from '@/components/bundle/DeleteAlertDialog';
import { EditBundleTree } from '@/components/bundle/edit/EditBundleTree';
import { GenerateBundleDialog } from '@/components/bundle/GenerateBundleDialog';
import { GeneratedDocsDialog } from '@/components/bundle/GeneratedDocsDialog';
import { TitlePageSettings, type TitlePageSettingsHandle } from '@/components/bundle/TitlePageSettings';
import { UnsavedChangesDialog } from '@/components/bundle/UnsavedChangesDialog';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import Loading from '@/components/shared/Loading';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import { Textarea } from '@/components/ui/textarea';
import { useBundle } from '@/hooks/useBundle';
import { useBundleFonts } from '@/hooks/useBundleFonts';
import { useBundlePreviewPanel } from '@/hooks/useBundlePreviewPanel';
import { hydrateBundleTree, useBundleRemoteRefresh } from '@/hooks/useBundleRemoteRefresh';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { type BundleBase, bundlePaths } from '@/lib/bundleRoutes';

const BundleEditorPage = ({ slug, bundleId, base }: { slug: string; bundleId: string; base: BundleBase }) => {
  const { selectedCase } = useCaseContext();
  const navigate = useNavigate();
  const paths = bundlePaths(base);
  const { ui_labels } = useMatterJurisdiction().jurisdiction;

  const { bundle, isLoading, isError, updateBundle, mutate } = useBundle(slug, bundleId);
  const editorMeta = useEditorMeta();
  const editTitle = editorMeta.title;
  const editDescription = editorMeta.description;
  const currentMetadata = editorMeta.metadata;
  const { setEditorTitle, setEditorDescription, setEditorMetadata, renameAutoVolumes } = useBundleTreeActions();
  const editFileCount = useBundleCounts().fileCount;
  const isDirty = useIsDirty();
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const skipGuardRef = useRef(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { handlePreviewClick, previewLoading, handlePreviewIndexClick, previewIndexLoading } =
    useBundlePreviewPanel(slug, bundleId);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showGeneratedDocsDialog, setShowGeneratedDocsDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [editTab, setEditTab] = useState<'structure' | 'settings'>('structure');

  const titlePageRef = useRef<TitlePageSettingsHandle>(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const fontOptions = useBundleFonts();
  const fontCssMap = useMemo(
    () => Object.fromEntries(fontOptions.map((f) => [f.value, f.fontFamily])),
    [fontOptions],
  );

  // Only re-initialize on bundle identity change. SWR revalidations produce a new `bundle`
  // reference with the same content; re-initializing on every revalidation would stomp the
  // user's in-progress edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (!bundle) return;
    hydrateBundleTree(slug, bundle);
  }, [bundle?.bundle_id]);

  useBundleRemoteRefresh({
    bundleId,
    matterGuid: slug,
    mutate,
    resourceNoun: ui_labels.bundle_singular.toLowerCase(),
    isDirty: () => isDirtyRef.current,
  });

  const handleGenerate = async (filename: string, targetFolderGuid: string | null) => {
    setIsGenerating(true);
    try {
      await backendClient.post(`/bundle/matter/${slug}/${bundleId}/generate`, {
        filename,
        target_folder_guid: targetFolderGuid,
      });
      setShowGenerateDialog(false);
      toast.success(ui_labels.bundle_generation_started);
    } catch (err: any) {
      toast.error(apiErrorMessage(err) || 'Failed to start generation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyEdit = useCallback(async () => {
    if (!editTitle.trim() || !currentMetadata) return false;

    setIsApplying(true);
    try {
      // Flush staged title page before metadata so a blob failure prevents stale metadata
      await titlePageRef.current?.flushTitlePage();
      const updated = await updateBundle({
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        metadata: currentMetadata,
        groups: toGroups(useBundleTreeStore.getState().config),
      });
      // Re-initialize from the response so the store picks up server-issued IDs in place
      // of any temp-... ids; otherwise a subsequent save would resend null for those nodes
      // and the backend would orphan-drop and recreate them with fresh UUIDs.
      hydrateBundleTree(slug, updated);
      toast.success(ui_labels.bundle_update_success);
      return true;
    } catch (error: any) {
      toast.error(apiErrorMessage(error) || ui_labels.bundle_save_failed);
      return false;
    } finally {
      setIsApplying(false);
    }
  }, [currentMetadata, editDescription, editTitle, updateBundle, ui_labels.bundle_update_success, ui_labels.bundle_save_failed]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await backendClient.delete(`/bundle/matter/${slug}/${bundleId}`);
      skipGuardRef.current = true;
      await navigate({ to: paths.index, params: { slug } });
    } catch (err: any) {
      setIsDeleting(false);
      toast.error(apiErrorMessage(err) || ui_labels.bundle_delete_failed);
    }
  };

  const guardDirty = useCallback((action: () => void) => {
    if (isDirty) {
      setPendingAction(() => action);
      return;
    }
    action();
  }, [isDirty]);

  const handleCancel = useCallback(() => {
    guardDirty(() => {
      navigate({ to: paths.detail, params: { slug, bundleId } });
    });
  }, [guardDirty, navigate, slug, bundleId]);

  const handlePreview = useCallback(async () => {
    if (editFileCount === 0) return;
    if (isDirty) {
      const ok = await handleApplyEdit();
      if (!ok) return;
    }
    handlePreviewClick();
  }, [editFileCount, isDirty, handleApplyEdit, handlePreviewClick]);

  const handlePreviewIndex = useCallback(async () => {
    if (editFileCount === 0) return;
    if (isDirty) {
      const ok = await handleApplyEdit();
      if (!ok) return;
    }
    handlePreviewIndexClick();
  }, [editFileCount, isDirty, handleApplyEdit, handlePreviewIndexClick]);

  const handleGenerateFromTabs = useCallback(async () => {
    if (editFileCount === 0) return;
    if (isDirty) {
      const ok = await handleApplyEdit();
      if (!ok) return;
    }
    setShowGenerateDialog(true);
  }, [editFileCount, isDirty, handleApplyEdit]);

  const confirmDiscard = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    skipGuardRef.current = true;
    action?.();
  }, [pendingAction]);

  const confirmSave = useCallback(async () => {
    const action = pendingAction;
    const ok = await handleApplyEdit();
    if (!ok) return;
    setPendingAction(null);
    skipGuardRef.current = true;
    action?.();
  }, [handleApplyEdit, pendingAction]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useBlocker({
    shouldBlockFn: ({ next }) => {
      if (!isDirty || skipGuardRef.current) {
        skipGuardRef.current = false;
        return false;
      }
      setPendingAction(() => () => navigate({ to: next.pathname, search: next.search }));
      return true;
    },
    enableBeforeUnload: false, // beforeunload is handled by the dedicated effect above
  });

  const breadcrumbSubheader = useMemo(() => (
    <span className="flex items-center text-gray-500">
      <Link to="/matter/$slug/case-files" params={{ slug }} className="hover:text-gray-700 font-medium">
        Case Files
      </Link>
      <span className="flex items-center">
        <ChevronRight className="h-4 w-4 mx-1" />
        <Link to={paths.index} params={{ slug }} className="hover:text-gray-700 font-medium">
          {ui_labels.bundle_plural}
        </Link>
      </span>
      <span className="flex items-center">
        <ChevronRight className="h-4 w-4 mx-1" />
        <span className="hover:text-gray-700 font-medium">{editTitle || ui_labels.bundle_singular}</span>
      </span>
    </span>
  ), [slug, editTitle, ui_labels.bundle_plural, ui_labels.bundle_singular]);

  useShellHeader({
    header: editTitle || ui_labels.bundle_singular,
    subheader: breadcrumbSubheader,
  });

  // Memoized so the shell header doesn't re-dispatch on every unrelated re-render.
  const editHeaderBelow = useMemo(
    () => (
      <div className="flex items-end justify-between gap-4">
        <HeaderTabs
          items={[
            { value: 'structure', label: 'Structure' },
            { value: 'settings', label: 'Settings' },
          ]}
          value={editTab}
          onChange={(value) => setEditTab(value as 'structure' | 'settings')}
          idBase="bundle-edit"
        />
        <div className="flex min-w-0 items-center justify-end gap-2 pb-2">
          <Button
            variant="ghost"
            size="sm"
            icon={ChevronLeft}
            text="Back"
            onClick={handleCancel}
            disabled={isApplying}
            className="bg-slate-100 hover:bg-slate-200"
          />
          <Button
            size="sm"
            onClick={handleApplyEdit}
            disabled={isApplying || !editTitle.trim() || !isDirty}
            loading={isApplying}
            text="Save"
            icon={Save}
          />
          <BundleToolbar
            hasFiles={editFileCount > 0}
            onPreview={handlePreview}
            previewLoading={previewLoading}
            onPreviewIndex={handlePreviewIndex}
            previewIndexLoading={previewIndexLoading}
            onGenerate={handleGenerateFromTabs}
            generateLoading={isGenerating}
            onGeneratedDocs={() => setShowGeneratedDocsDialog(true)}
          />
        </div>
      </div>
    ),
    [
      editTab,
      handleCancel,
      isApplying,
      handleApplyEdit,
      editTitle,
      isDirty,
      editFileCount,
      handlePreview,
      previewLoading,
      handlePreviewIndex,
      previewIndexLoading,
      handleGenerateFromTabs,
      isGenerating,
    ],
  );

  useShellHeaderBelow(selectedCase && bundle && currentMetadata ? editHeaderBelow : null);

  if (!selectedCase) return <NoCaseSelected />;
  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><Loading /></div>;
  }
  if (!bundle || !currentMetadata) {
    const status = isError?.response?.status;
    const noun = ui_labels.bundle_singular.toLowerCase();
    const message = status === 404
      ? `This ${noun} does not exist.`
      : status === 403
        ? `You do not have permission to view this ${noun}.`
        : `Failed to load ${noun}.`;
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <p className="text-sm text-gray-500">{message}</p>
        <Button variant="ghost" size="sm" icon={ChevronLeft} text={ui_labels.bundle_back} onClick={() => navigate({ to: paths.index, params: { slug } })} />
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 pb-8">
      <UnsavedChangesDialog
        open={pendingAction !== null}
        isSaving={isApplying}
        onSave={confirmSave}
        onDiscard={confirmDiscard}
        onCancel={() => setPendingAction(null)}
      />
      <div
        role="tabpanel"
        id="bundle-edit-panel-structure"
        aria-labelledby="bundle-edit-tab-structure"
        tabIndex={0}
        hidden={editTab !== 'structure'}
        className="focus-visible:outline-hidden"
      >
          <div className="mx-auto mt-3 w-full max-w-(--breakpoint-xl) lg:my-8">
            {/* The child selector widens the gap inside EditBundleTree's root flex
                column, between its toolbar row and the tree itself. */}
            <section className="rounded-xl border border-border bg-card px-6 pt-8 pb-6 [&>div]:gap-6">
              <EditBundleTree />
            </section>
          </div>
        </div>
        <div
          role="tabpanel"
          id="bundle-edit-panel-settings"
          aria-labelledby="bundle-edit-tab-settings"
          tabIndex={0}
          hidden={editTab !== 'settings'}
          className="focus-visible:outline-hidden"
        >
          <div className="mx-auto mt-6 w-full max-w-(--breakpoint-xl)">
            <div className="space-y-6">
              <section className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <h3 className="text-base font-semibold text-slate-900">Overview</h3>
                    <p className="text-sm text-slate-600 mt-1 mr-6 md:mr-0">{ui_labels.bundle_overview_hint}</p>
                  </div>
                  <div className="md:col-span-2 space-y-4 md:pt-16">
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">{ui_labels.bundle_name_label}</p>
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditorTitle(e.target.value)}
                        placeholder={ui_labels.bundle_title_placeholder}
                        className="max-w-xl"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Description</p>
                      <Textarea
                        value={editDescription}
                        onChange={(e) => setEditorDescription(e.target.value)}
                        placeholder={ui_labels.bundle_description_placeholder}
                        className="max-w-xl resize-none"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              </section>
              <section className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <h3 className="text-base font-semibold text-slate-900">Title Page</h3>
                    <p className="text-sm text-slate-600 mt-1 mr-6 md:mr-0">Edit title page template or upload your own.</p>
                  </div>
                  <div className="md:col-span-2 md:pt-16">
                    <TitlePageSettings
                      ref={titlePageRef}
                      settings={currentMetadata}
                      onChange={setEditorMetadata}
                      matterSlug={slug}
                      bundleId={bundleId}
                      serverUpdatedAt={bundle.updated_at}
                      fontOptions={fontOptions}
                    />
                  </div>
                </div>
              </section>
              <section className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <h3 className="text-base font-semibold text-slate-900">Formatting</h3>
                    <p className="text-sm text-slate-600 mt-1 mr-6 md:mr-0">Configure volumes, the index, page stamps, and other output options.</p>
                  </div>
                  <div className="md:col-span-2 md:pt-16">
                    <BundleSettingsPanel
                      settings={currentMetadata}
                      onChange={(metadata) => {
                        const oldNaming = currentMetadata.volume_naming;
                        setEditorMetadata(metadata);
                        if (metadata.volume_naming !== oldNaming) {
                          renameAutoVolumes(metadata.volume_naming);
                        }
                      }}
                      fontOptions={fontOptions}
                      fontCssMap={fontCssMap}
                    />
                  </div>
                </div>
              </section>
              <div className="w-full bg-white px-1 py-3">
                <div className="flex w-full flex-wrap items-center justify-between gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(true)} text="Delete" icon={Trash2} className="text-crimson-600 border-crimson-200 hover:bg-crimson-600 hover:text-white transition-colors" />
                </div>
              </div>
            </div>
          </div>
        </div>
      <DeleteAlertDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={bundle?.title ?? ''}
        isDeleting={isDeleting}
        onDelete={handleDelete}
      />
      <GenerateBundleDialog
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        bundleTitle={bundle?.title ?? ui_labels.bundle_singular}
        matterSlug={slug}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
      />
      <GeneratedDocsDialog
        open={showGeneratedDocsDialog}
        onOpenChange={setShowGeneratedDocsDialog}
        matterSlug={slug}
        bundleId={bundleId}
      />
    </div>
  );
};
// `binders` is the US-facing alias for `bundles`; both routes render the same page.
// Each route gets its own wrapper so params are read with a concrete, typed `from`
// (no `strict: false` + cast needed).
export const BundleEditPage = () => {
  const { slug, bundleId } = useParams({ from: '/matter/$slug/bundles/$bundleId/edit' });
  return <BundleEditorPage slug={slug} bundleId={bundleId} base="bundles" />;
};

export const BinderEditPage = () => {
  const { slug, bundleId } = useParams({ from: '/matter/$slug/binders/$bundleId/edit' });
  return <BundleEditorPage slug={slug} bundleId={bundleId} base="binders" />;
};

export default BundleEditPage;
